import React from 'react'
import { ChipTypeMap, styled, unstable_composeClasses } from '@mui/material'
import classnames from 'classnames'

import AutocompleteField, { AutocompleteFieldProps, AutocompleteOption } from '../AutocompleteField'
import autocompleteFieldClasses from '../AutocompleteField/autoCompleteFieldClasses'
import { getGlobalSearchFieldUtilityClass } from './globalSearchFieldClasses'

export interface GlobalSearchFieldProps<
  Option extends AutocompleteOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
  QueryVariables extends Record<string, unknown> = Record<string, unknown>,
  MoreQueryVariables extends Record<string, unknown> = Record<string, unknown>,
  QueryResults extends unknown = Record<string, unknown>,
  MoreQueryResults extends unknown = QueryResults,
> extends AutocompleteFieldProps<
    Option,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent,
    QueryVariables,
    MoreQueryVariables,
    QueryResults,
    MoreQueryResults
  > {}

const GlobalSearchFieldRoot = styled(AutocompleteField, {
  name: 'GlobalSearchField',
  slot: 'Root',
  overridesResolver: (_, styles) => styles.root,
})(({ theme }) => ({
  [`> .${autocompleteFieldClasses.field}`]: {
    width: '100%',
    padding: theme.spacing(2),
    background: theme.colours.new.white,
    borderRadius: theme.display.borderRadius,
    [`> .${autocompleteFieldClasses.searchIcon}`]: {
      width: '20px',
      height: '20px',
    },
    '> .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  [`& .${autocompleteFieldClasses.field} .${autocompleteFieldClasses.select}`]: {
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(18),
  },
  [`& .${autocompleteFieldClasses.endAdornment}`]: {
    '> button': {
      fontSize: theme.typography.pxToRem(16),
      lineHeight: theme.typography.pxToRem(18),
      marginRight: theme.spacing(2),
    },
  },
})) as typeof AutocompleteField

const useUtilityClasses = <
  Option extends AutocompleteOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  QueryVariables extends Record<string, unknown> = Record<string, unknown>,
  MoreQueryVariables extends Record<string, unknown> = Record<string, unknown>,
  QueryResults extends unknown = Record<string, unknown>,
  MoreQueryResults extends unknown = QueryResults,
>(
  ownerState: Partial<
    GlobalSearchFieldProps<
      Option,
      Multiple,
      DisableClearable,
      FreeSolo,
      any,
      QueryVariables,
      MoreQueryVariables,
      QueryResults,
      MoreQueryResults
    >
  >,
) => {
  const slots = {
    root: ['root'],
  }

  return unstable_composeClasses(slots, getGlobalSearchFieldUtilityClass, ownerState.classes)
}

const _GlobalSearchField = function _GlobalSearchField<
  Option extends AutocompleteOption = AutocompleteOption,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = true,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
  QueryVariables extends Record<string, unknown> = Record<string, unknown>,
  MoreQueryVariables extends Record<string, unknown> = Record<string, unknown>,
  QueryResults extends unknown = Record<string, unknown>,
  MoreQueryResults extends unknown = QueryResults,
>(
  props: GlobalSearchFieldProps<
    Option,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent,
    QueryVariables,
    MoreQueryVariables,
    QueryResults,
    MoreQueryResults
  >,
) {
  const { className, classes, ...rootProps } = props

  const slotClasses = useUtilityClasses({ classes })

  return (
    <GlobalSearchFieldRoot
      asyncMode="search"
      slotProps={{
        popper: {
          // setting the width with sx doesn't fully work so we need to set it here
          style: { maxWidth: '960px', width: '100%' },
          sx(theme) {
            return {
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
            }
          },
        },
        paper: {
          sx(theme) {
            return {
              marginTop: theme.spacing(2),
              [`& .${autocompleteFieldClasses.list}`]: {
                padding: theme.spacing(2),
              },
            }
          },
        },
      }}
      className={classnames(slotClasses.root, className)}
      {...rootProps}
    />
  )
}

export const GlobalSearchField = React.memo(_GlobalSearchField) as typeof _GlobalSearchField

export default GlobalSearchField
